// "Not sent yet": per app, releases that are out but not deployed and the
// release bot's open release PR, drawn with the same app panel as the sent
// updates. Read-only, like the rest of the page.

import { AppPanel, kbGroups, type CopyFormat } from './UpdateCard';
import { KbChanges } from './KbChanges';
import { CopyButton, Notice } from './parts';
import { appMarkdown, appText, type PendingApp } from '../data/changelog';

export function Pending({ apps, prAccess, isOpen, onToggle, format, filtered }: {
  apps: PendingApp[];
  prAccess: 'ok' | 'no_access' | 'unavailable';
  isOpen: (app: string) => boolean;
  onToggle: (app: string) => void;
  format: CopyFormat;
  /** How many apps are chosen in "On Scouts now" (0: every app). */
  filtered: number;
}) {
  const allText = () => {
    const parts = apps.map((a) => (format === 'markdown' ? appMarkdown(a) : appText(a)));
    return format === 'markdown' ? ['## Not on Scouts yet', ...parts].join('\n\n') : ['Not on Scouts yet', '', ...parts].join('\n\n');
  };
  return (
    <section aria-label="Not sent to Scouts yet" className="space-y-3">
      {prAccess === 'no_access' && (
        <Notice tone="info">
          <strong className="font-semibold text-white">Release PRs aren't shown yet.</strong> Give the Vortex changelog reader GitHub app
          Pull requests: Read to list what is waiting in release PRs. Releases that are out but not sent are listed already.
        </Notice>
      )}
      {apps.length === 0 ? (
        <p className="text-[15px] text-slate-400">
          {filtered ? `Nothing for ${filtered === 1 ? 'this app' : 'these apps'} is waiting to go out.` : 'Nothing is waiting. Every release has gone out to Scouts, and no release PRs are open.'}
        </p>
      ) : (
        <div className="rounded-2xl border border-amber-400/25 bg-slate-800/50">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-700/60 px-4 py-3">
            <span className="flex-1 text-[15px] text-slate-300">
              <strong className="font-semibold text-white">{apps.length} {apps.length === 1 ? 'app has' : 'apps have'} changes Scouts don't have yet.</strong>{' '}
              <span className="text-slate-400">Release PRs update as work lands on main.</span>
            </span>
            <CopyButton getText={allText} ariaLabel="Copy everything not on Scouts yet" />
          </div>
          <div className="space-y-2.5 p-2 sm:p-3">
            <KbChanges groups={kbGroups(apps)} id="pending-kb" heading="Knowledge base changes, not on Scouts yet" format={format} />
            {apps.map((a) => (
              <div key={a.app} id={`pending-${a.app}`} className="scroll-mt-20">
                <AppPanel update={a} open={isOpen(a.app)} onToggle={() => onToggle(a.app)} format={format} panelId={`pending-${a.app}`} pending={{ pr: a.pr }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
